import { supabase } from './supabase';

/**
 * Device tracking — anti-sharing controls.
 *
 * Each browser persists a unique device_id in localStorage. On login and
 * on app mount we register the device with the server. If the user has
 * more than 5 active devices, the server auto-revokes the oldest.
 *
 * Revocation is checked on app mount via check_device_revoked(). If the
 * current device has been revoked (either by the user from another
 * device, or auto-revoked because of the cap), we sign out and route
 * them to the login page with a friendly explanation.
 *
 * This is deliberately NOT a hard server-side block — Supabase Auth
 * sessions stay valid until expiry. We're raising the cost of credential
 * sharing, not engineering a Fort Knox. The bigger deterrent is that
 * every record in the app is signed by user account, so shared logins
 * produce evidence of fraud, not protection from prosecution.
 */

const DEVICE_ID_KEY = 'sas_device_id';

/** Returns the persistent device id for this browser. Generates one on first use. */
export function getOrCreateDeviceId(): string {
  try {
    let id = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      window.localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    // Private mode / storage disabled — use an ephemeral id that resets per page load.
    return crypto.randomUUID();
  }
}

/** Friendly device label derived from the UA. "Chrome on macOS" / "Safari on iPhone". */
export function getDeviceLabel(): string {
  if (typeof navigator === 'undefined') return 'Unknown device';
  const ua = navigator.userAgent || '';

  // Browser
  let browser = 'Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera\//.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  // OS
  let os = 'device';
  if (/iPhone/.test(ua)) os = 'iPhone';
  else if (/iPad/.test(ua)) os = 'iPad';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Macintosh|Mac OS X/.test(ua)) os = 'macOS';
  else if (/Windows/.test(ua)) os = 'Windows';
  else if (/Linux/.test(ua)) os = 'Linux';

  return `${browser} on ${os}`;
}

export interface RegisterResult {
  deviceId: string;
  wasRevoked: boolean;          // this device had been revoked → cleared by re-registering
  activeCount: number;
  capExceeded: boolean;          // 6th-device registration → oldest auto-revoked
}

/** Call on login + on app mount. Upserts the device row server-side. */
export async function registerCurrentDevice(): Promise<RegisterResult | null> {
  try {
    const deviceId = getOrCreateDeviceId();
    const { data, error } = await supabase.rpc('register_device', {
      p_device_id:  deviceId,
      p_label:      getDeviceLabel(),
      p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    });
    if (error) {
      console.warn('[registerCurrentDevice] failed:', error.message);
      return null;
    }
    const row = (data as Array<{
      device_id: string;
      was_revoked: boolean;
      active_count: number;
      cap_exceeded: boolean;
    }> | null)?.[0];
    if (!row) return null;
    return {
      deviceId:     row.device_id,
      wasRevoked:   row.was_revoked,
      activeCount:  row.active_count,
      capExceeded:  row.cap_exceeded,
    };
  } catch (e) {
    console.warn('[registerCurrentDevice] threw:', e);
    return null;
  }
}

export interface RevocationStatus {
  revoked: boolean;
  revokedAt: string | null;
  revokedReason: 'user_revoke' | 'cap_exceeded' | 'admin_revoke' | 'suspected_share' | null;
}

/** Cheap server check. Returns {revoked: true} if this device has been revoked. */
export async function checkDeviceRevoked(): Promise<RevocationStatus> {
  const deviceId = getOrCreateDeviceId();
  const { data, error } = await supabase.rpc('check_device_revoked', { p_device_id: deviceId });
  if (error) {
    // Soft-fail: don't sign the user out just because the check itself errored
    return { revoked: false, revokedAt: null, revokedReason: null };
  }
  const row = (data as Array<{
    revoked: boolean;
    revoked_at: string | null;
    revoked_reason: RevocationStatus['revokedReason'];
  }> | null)?.[0];
  if (!row) return { revoked: false, revokedAt: null, revokedReason: null };
  return {
    revoked:       row.revoked,
    revokedAt:     row.revoked_at,
    revokedReason: row.revoked_reason,
  };
}

export interface UserDevice {
  id: string;
  deviceId: string;
  label: string | null;
  userAgent: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  revokedAt: string | null;
  revokedReason: RevocationStatus['revokedReason'];
  isThisDevice: boolean;
}

export async function listMyDevices(): Promise<UserDevice[]> {
  const currentDeviceId = getOrCreateDeviceId();
  const { data, error } = await supabase
    .from('user_devices')
    .select('id, device_id, label, user_agent, first_seen_at, last_seen_at, revoked_at, revoked_reason')
    .order('last_seen_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((d) => {
    const row = d as {
      id: string;
      device_id: string;
      label: string | null;
      user_agent: string | null;
      first_seen_at: string;
      last_seen_at: string;
      revoked_at: string | null;
      revoked_reason: RevocationStatus['revokedReason'];
    };
    return {
      id:           row.id,
      deviceId:     row.device_id,
      label:        row.label,
      userAgent:    row.user_agent,
      firstSeenAt:  row.first_seen_at,
      lastSeenAt:   row.last_seen_at,
      revokedAt:    row.revoked_at,
      revokedReason: row.revoked_reason,
      isThisDevice: row.device_id === currentDeviceId,
    };
  });
}

export async function revokeDevice(deviceId: string): Promise<void> {
  const { error } = await supabase.rpc('revoke_device', { p_device_id: deviceId });
  if (error) throw error;
}

export async function revokeOtherDevices(): Promise<number> {
  const keep = getOrCreateDeviceId();
  const { data, error } = await supabase.rpc('revoke_other_devices', { p_keep_device_id: keep });
  if (error) throw error;
  return (data as number | null) ?? 0;
}
